import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import '@/App.css';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Home from '@/pages/Home';
import Languages from '@/pages/Languages';
import LanguageHub from '@/pages/LanguageHub';
import Pricing from '@/pages/Pricing';
import Schools from '@/pages/Schools';
import Contact from '@/pages/Contact';
import Legal from '@/pages/Legal';
import Register from '@/pages/Register';
import Login from '@/pages/Login';
import AddChild from '@/pages/AddChild';
import Dashboard from '@/pages/Dashboard';
import Admin from '@/pages/Admin';
import Redeem from '@/pages/Redeem';
import AffiliateApply from '@/pages/AffiliateApply';
import AffiliateDashboard from '@/pages/AffiliateDashboard';
import StudentJoin from '@/pages/StudentJoin';
import Teacher from '@/pages/Teacher';
import SubscriptionReturn from '@/pages/SubscriptionReturn';
import StripeReturn from '@/pages/StripeReturn';
import Support from '@/pages/Support';
import SupportThankYou from '@/pages/SupportThankYou';
import About from '@/pages/About';
import AdvertiseWithUs from '@/pages/AdvertiseWithUs';
import Parents from '@/pages/Parents';
import LearnLanguage from '@/pages/LearnLanguage';
import SecretViralWelcome from '@/pages/SecretViralWelcome';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import Leaderboard from '@/pages/Leaderboard';
import FAQ from '@/pages/FAQ';
import Help from '@/pages/Help';
import Account from '@/pages/Account';
import Countries from '@/pages/Countries';
import CountryHub from '@/pages/CountryHub';
import CountryQuiz from '@/pages/CountryQuiz';
import AdminCountries from '@/pages/AdminCountries';
import AdminAudioStatus from '@/pages/AdminAudioStatus';
import AdminOperations from '@/pages/AdminOperations';
import EmailPreferences from '@/pages/EmailPreferences';
import ChangePasswordRequired from '@/pages/ChangePasswordRequired';
import NotFound from '@/pages/NotFound';
import { WordSearch, BalloonPop, Crossword, WordScramble, MissingVowel, Cryptogram } from '@/pages/Play';
import FreeTierGate from '@/components/FreeTierGate';
import AffiliateRefTracker from '@/components/AffiliateRefTracker';

function Shell({ children }) {
    const location = useLocation();
    // Hide chrome on routes that should not offer escape hatches (e.g.
    // forced password change for admin-provisioned teachers).
    const chromeHidden = location.pathname === '/onboarding/change-password';
    return (
        <div className="min-h-screen flex flex-col bg-lq-paper">
            {!chromeHidden && <Header />}
            <div className="flex-1">{children}</div>
            {!chromeHidden && <Footer />}
        </div>
    );
}
function RequireAuth({ children }) {
    const { user, loading } = useAuth();
    const location = useLocation();
    if (loading) return <div className="max-w-3xl mx-auto px-6 py-20 text-lq-ink-500">Loading...</div>;
    if (!user) return <Navigate to="/login" replace />;
    // Force first-time password change for admin-provisioned accounts (teachers, etc.).
    if (user.must_change_password && location.pathname !== '/onboarding/change-password') {
        return <Navigate to="/onboarding/change-password" replace />;
    }
    return children;
}

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <AffiliateRefTracker />
                <Shell>
                    <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/languages" element={<Languages />} />
                        <Route path="/languages/:slug" element={<LanguageHub />} />
                        <Route path="/pricing" element={<Pricing />} />
                        <Route path="/schools" element={<Schools />} />
                        <Route path="/schools/join" element={<StudentJoin />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/advertise" element={<AdvertiseWithUs />} />
                        <Route path="/parents" element={<Parents />} />
                        <Route path="/learn-shona-for-kids" element={<LearnLanguage slug="shona" />} />
                        <Route path="/learn-ndebele-for-kids" element={<LearnLanguage slug="ndebele" />} />
                        <Route path="/learn-yoruba-for-kids" element={<LearnLanguage slug="yoruba" />} />
                        <Route path="/learn-swahili-for-kids" element={<LearnLanguage slug="swahili" />} />
                        <Route path="/learn-french-for-kids" element={<LearnLanguage slug="french" />} />
                        <Route path="/learn-portuguese-for-kids" element={<LearnLanguage slug="portuguese" />} />
                        <Route path="/learn-spanish-for-kids" element={<LearnLanguage slug="spanish" />} />
                        <Route path="/secret-viral-welcome" element={<SecretViralWelcome />} />
                        <Route path="/redeem" element={<Redeem />} />
                        <Route path="/affiliates/apply" element={<AffiliateApply />} />
                        <Route path="/affiliates/dashboard" element={<RequireAuth><AffiliateDashboard /></RequireAuth>} />
                        <Route path="/legal/:slug" element={<Legal />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/faq" element={<FAQ />} />
                        <Route path="/help" element={<Help />} />
                        <Route path="/leaderboard" element={<Leaderboard />} />
                        <Route path="/subscription/return" element={<SubscriptionReturn />} />
                        <Route path="/subscription/stripe-return" element={<RequireAuth><StripeReturn /></RequireAuth>} />
                        <Route path="/support" element={<Support />} />
                        <Route path="/support/thank-you" element={<SupportThankYou />} />
                        <Route path="/onboarding/add-child" element={<RequireAuth><AddChild /></RequireAuth>} />
                        <Route path="/onboarding/change-password" element={<ChangePasswordRequired />} />
                        <Route path="/account/email-preferences" element={<EmailPreferences />} />
                        <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
                        <Route path="/account" element={<RequireAuth><Account /></RequireAuth>} />
                        <Route path="/countries" element={<Countries />} />
                        <Route path="/countries/:slug" element={<CountryHub />} />
                        <Route path="/countries/:slug/quiz" element={<RequireAuth><CountryQuiz /></RequireAuth>} />
                        <Route path="/admin/countries" element={<RequireAuth><AdminCountries /></RequireAuth>} />
                        <Route path="/admin/audio-status" element={<RequireAuth><AdminAudioStatus /></RequireAuth>} />
                        <Route path="/admin/operations" element={<RequireAuth><AdminOperations /></RequireAuth>} />
                        <Route path="/teacher" element={<RequireAuth><Teacher /></RequireAuth>} />
                        <Route path="/admin" element={<RequireAuth><Admin /></RequireAuth>} />
                        <Route path="/play/:language/:theme/:ageTier/word_search" element={<FreeTierGate puzzleType="word_search"><WordSearch /></FreeTierGate>} />
                        <Route path="/play/:language/:theme/:ageTier/balloon_pop" element={<RequireAuth><FreeTierGate puzzleType="balloon_pop"><BalloonPop /></FreeTierGate></RequireAuth>} />
                        <Route path="/play/:language/:theme/:ageTier/crossword" element={<RequireAuth><FreeTierGate puzzleType="crossword"><Crossword /></FreeTierGate></RequireAuth>} />
                        <Route path="/play/:language/:theme/:ageTier/word_scramble" element={<RequireAuth><FreeTierGate puzzleType="word_scramble"><WordScramble /></FreeTierGate></RequireAuth>} />
                        <Route path="/play/:language/:theme/:ageTier/missing_vowel" element={<RequireAuth><FreeTierGate puzzleType="missing_vowel"><MissingVowel /></FreeTierGate></RequireAuth>} />
                        <Route path="/play/:language/:theme/:ageTier/cryptogram" element={<RequireAuth><FreeTierGate puzzleType="cryptogram"><Cryptogram /></FreeTierGate></RequireAuth>} />
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </Shell>
            </BrowserRouter>
        </AuthProvider>
    );
}
export default App;
